/*
 *  Copyright (C) 2026 Atelier Cartographique <contact@atelier-cartographique.be>
 *
 *  This program is free software: you can redistribute it and/or modify
 *  it under the terms of the GNU General Public License as published by
 *  the Free Software Foundation, version 3 of the License.
 *
 *  This program is distributed in the hope that it will be useful,
 *  but WITHOUT ANY WARRANTY; without even the implied warranty of
 *  MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 *  GNU General Public License for more details.
 *
 *  You should have received a copy of the GNU General Public License
 *  along with this program.  If not, see <http://www.gnu.org/licenses/>.
 */

import { right } from 'fp-ts/lib/Either';
import { fromNullable, some } from 'fp-ts/lib/Option';
import { addFeaturesToLayer, addLayer, removeLayerAll } from 'sdi/map';
import { dispatch, observe } from 'sdi/shape/functions';
import {
    getMessageRecord,
    ILayerInfo,
    Inspire,
    withQueryString,
} from 'sdi/source';
import { mapName } from '../components/map';
import { getLayerInfoList, getMapInfo } from '../queries/app';
import { getView } from '../queries/map';
import { fetchLayer } from '../remote';
import { withMetadata } from './data';

// this is a gentle hack
const _inFlightLayers: Record<string, boolean> = {};

const _startLoadingLayer = (id: string) => (_inFlightLayers[id] = true);
const _endLoadingLayer = (id: string) => (_inFlightLayers[id] = false);
const _layerShouldLoad = (id: string) => !(id in _inFlightLayers);

const loadFullLayer = (info: ILayerInfo, metadata: Inspire) => {
    if (_layerShouldLoad(info.id)) {
        _startLoadingLayer(info.id);
        fetchLayer(metadata.uniqueResourceIdentifier).then(layer => {
            _endLoadingLayer(info.id);

            dispatch('data/layer', state => [...state, [info.id, layer]]);

            addLayer(
                mapName,
                () =>
                    some({
                        name: getMessageRecord(metadata.resourceTitle),
                        info,
                        metadata,
                    }),
                () => right(some(layer)),
            );
        });
    }
};

const loadStreamLayer = (
    info: ILayerInfo,
    metadata: Inspire,
    bbox: number[],
) => {
    const url = withQueryString(metadata.uniqueResourceIdentifier, {
        bbox: `${bbox[0]},${bbox[1]},${bbox[2]},${bbox[3]}`,
    });

    fetchLayer(url).then(layer => {
        // if layer already set, it's a noop
        addLayer(
            mapName,
            () =>
                some({
                    name: getMessageRecord(metadata.resourceTitle),
                    info,
                    metadata,
                }),
            () => right(some({ type: 'FeatureCollection', features: [] })),
        );

        addFeaturesToLayer(mapName, info, layer.features);

        // storing last batch of data for this layer, maybe superfluous
        dispatch('data/layer', state => {
            const layerIndex = state.findIndex(x => x[0] === info.id);

            if (layerIndex >= 0) {
                state[layerIndex] = [info.id, layer];
            } else {
                state.push([info.id, layer]);
            }

            return state;
        });
    });
};

const isZoomVisible = (
    zoom: number,
    minZoom: number,
    maxZoom: number,
): boolean => {
    return zoom > minZoom && zoom < maxZoom;
};

const loadLayerData = (layer: ILayerInfo, metadata: Inspire) => {
    const zoom = getView().zoom;
    const minZoom = fromNullable(layer.minZoom).getOrElse(0);
    const maxZoom = fromNullable(layer.maxZoom).getOrElse(30);

    if (metadata.dataStreamUrl === null) {
        loadFullLayer(layer, metadata);
    } else {
        const extent = getView().extent;
        if (extent !== null && isZoomVisible(zoom, minZoom, maxZoom)) {
            loadStreamLayer(layer, metadata, extent);
        }
    }
};

export const loadVisibleLayers = (layers: ILayerInfo[]) => {
    removeLayerAll(mapName);
    layers.forEach(layerInfo => {
        if (layerInfo.visible) {
            withMetadata(layerInfo.metadataId, md => {
                loadLayerData(layerInfo, md);
            });
        }
    });
};

observe('port/map/view', () => {
    getMapInfo().fold(null, () => {
        getLayerInfoList().forEach(layerInfo => {
            if (layerInfo.visible) {
                withMetadata(layerInfo.metadataId, md => {
                    if (md.dataStreamUrl) {
                        loadLayerData(layerInfo, md);
                    }
                });
            }
        });
    });
});
